/**
 * Practice-switcher fill logic for the Today worklist.
 *
 * Faithful port of Flutter `handlePracticeList` + `organisationWidget`
 * (scrips_msp1_pa appointment_list_widget.dart:178-215, 714-790):
 *   - dedupe by `practiceSetupId` (the API can return the same practice twice),
 *   - sort by `practiceName` (case-insensitive, Flutter `compareTo`),
 *   - selection precedence: the current/login practice if present → `isPrimary`
 *     → first.
 * Rows without a `practiceSetupId` are dropped — the id scopes the appointment
 * query, so an option without it is unusable.
 */
import type { PracticePickerOption } from '@scripsteam/scrips-signal-ds';
import type { PracticeListItem } from '@/types/practice-api';

/**
 * Deduped (by id) + name-sorted picker options, carrying the practice image so
 * the DS `PracticePicker` shows the practice avatar (Flutter organisationWidget
 * CircleImage). Empty when there are no usable rows.
 */
export function buildPracticeOptions(
  practices: PracticeListItem[] | undefined,
): PracticePickerOption[] {
  if (!practices?.length) return [];
  const seen = new Set<string>();
  const deduped = practices.filter((p) => {
    if (!p.practiceSetupId || seen.has(p.practiceSetupId)) return false;
    seen.add(p.practiceSetupId);
    return true;
  });
  deduped.sort((a, b) =>
    (a.practiceName ?? '').localeCompare(b.practiceName ?? '', undefined, {
      sensitivity: 'base',
    }),
  );
  // practiceSetupId is non-null past the filter above.
  return deduped.map((p) => ({
    label: p.practiceName ?? p.practiceSetupId!,
    value: p.practiceSetupId!,
    imageUrl: p.practiceImageURL,
  }));
}

/**
 * Which practice is selected. Flutter precedence: keep `current` (initialised to
 * the login practiceId) when it's in the list, else the `isPrimary` practice,
 * else the first option.
 */
export function resolveSelectedPracticeId(
  options: PracticePickerOption[],
  practices: PracticeListItem[] | undefined,
  current: string,
): string {
  if (!options.length) return current;
  if (current && options.some((o) => o.value === current)) return current;
  const primary = practices?.find((p) => p.isPrimary && p.practiceSetupId);
  return primary?.practiceSetupId ?? options[0]?.value ?? current;
}
